import { z } from 'zod';

/**
 * Project and model details beyond what Core serves today: key-value properties and
 * milestones. Proposed contract, see docs/project-details-proposal.md. Both are read by any
 * member and replaced whole by a project admin with `If-Match`.
 */
export const MILESTONE_STATUSES = ['Planned', 'AtRisk', 'Done'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

export const propertySchema = z.object({
  key: z.string().trim().min(1).max(100),
  value: z.string().max(1000),
});
export const propertiesSchema = z.object({
  items: z.array(propertySchema).max(100),
  etag: z.string().min(1),
});
export type Property = z.infer<typeof propertySchema>;
export type Properties = z.infer<typeof propertiesSchema>;

export const milestoneSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1).max(200),
  /** `YYYY-MM-DD`: a deadline is a day, not an instant. */
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** A status this client does not know yet reads as `Planned`. */
  status: z.enum(MILESTONE_STATUSES).catch('Planned'),
  /** The design phase it belongs to, e.g. "Stage 3". */
  phase: z.string().max(100).nullish().transform((value) => value ?? ''),
  /** What it depends on, or any other detail. */
  notes: z.string().max(1000).nullish().transform((value) => value ?? ''),
});
export const milestonesSchema = z.object({
  items: z.array(milestoneSchema).max(200),
  etag: z.string().min(1),
});
export type Milestone = z.infer<typeof milestoneSchema>;
export type Milestones = z.infer<typeof milestonesSchema>;

/** The project members assigned to one linked model, replaced whole like the lists above. */
export const modelMemberSchema = z.object({
  subject: z.string().min(1),
  displayName: z.string(),
  position: z.string().nullish().transform((value) => value ?? null),
});
export const modelMembersSchema = z.object({
  items: z.array(modelMemberSchema),
  etag: z.string().min(1),
});
export type ModelMember = z.infer<typeof modelMemberSchema>;
export type ModelMembers = z.infer<typeof modelMembersSchema>;
